import jsPDF from "jspdf";
import type { JobLogEntry } from "@/hooks/use-job-log";

const typeLabels: Record<string, string> = {
  "voltage-drop": "Voltage Drop",
  "pipe-velocity": "Pipe Velocity",
  "btu-sizing": "BTU Sizing",
  "conduit-fill": "Conduit Fill",
  "conduit-bending": "Conduit Bending",
  "ampacity-derate": "Ampacity Derate",
  "box-fill": "Box Fill",
  "transformer-kva": "Transformer kVA",
  "motor-starter": "Motor Starter",
  "motor-feeder": "Motor Feeder",
  "wire-sizing": "Wire Sizing",
  "egc-lookup": "EGC Lookup",
  "service-entrance": "Service Entrance",
  "panel-schedule": "Panel Schedule",
  "demand-factor": "Demand Factor",
  "fault-current": "Fault Current",
  "vfd-sizing": "VFD Sizing",
  "disconnect-sizing": "Disconnect Sizing",
  "power-factor-correction": "Power Factor Correction",
};

const MARGIN = 20;
const PAGE_W = 210;
const CONTENT_W = PAGE_W - MARGIN * 2;

export const generateJobLogPDF = (entries: JobLogEntry[]) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  const checkPage = (needed: number) => {
    if (y + needed > 280) {
      doc.addPage();
      y = MARGIN;
    }
  };

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(40, 40, 40);
  doc.text("Job Log Report", MARGIN, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(
    `${entries.length} calculation${entries.length !== 1 ? "s" : ""} · Generated ${new Date().toLocaleString()}`,
    MARGIN,
    y
  );
  y += 4;

  // Divider
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 8;

  entries.forEach((entry, idx) => {
    const date = new Date(entry.timestamp);
    const dateStr = date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });

    // Estimate height needed for this entry
    const inputCount = Object.keys(entry.inputs).length;
    const resultCount = entry.results.length;
    const estHeight = 30 + inputCount * 5 + resultCount * 5 + (entry.note ? 8 : 0);
    checkPage(estHeight);

    // Entry header background
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(MARGIN, y - 2, CONTENT_W, 10, 1.5, 1.5, "F");

    // Entry number + type
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    const label = typeLabels[entry.calcType] || entry.calcType;
    doc.text(`${idx + 1}. ${label}`, MARGIN + 3, y + 5);

    // Date on right
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`${dateStr} ${timeStr}`, PAGE_W - MARGIN - 3, y + 5, { align: "right" });
    y += 13;

    // Label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text(entry.label, MARGIN + 3, y);
    y += 5;

    // Note
    if (entry.note) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const noteLines = doc.splitTextToSize(`Note: ${entry.note}`, CONTENT_W - 6);
      doc.text(noteLines, MARGIN + 3, y);
      y += noteLines.length * 4 + 2;
    }

    // Inputs section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text("INPUTS", MARGIN + 3, y);
    y += 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    Object.entries(entry.inputs).forEach(([k, v]) => {
      checkPage(5);
      doc.setTextColor(100, 100, 100);
      doc.text(k, MARGIN + 6, y);
      doc.setTextColor(40, 40, 40);
      doc.text(String(v), MARGIN + 60, y);
      y += 4.5;
    });
    y += 2;

    // Results section
    checkPage(10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text("RESULTS", MARGIN + 3, y);
    y += 4;

    entry.results.forEach((r) => {
      checkPage(5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(r.label, MARGIN + 6, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text(`${r.value} ${r.unit}`, MARGIN + 60, y);
      y += 4.5;
    });

    y += 4;

    // Separator between entries
    if (idx < entries.length - 1) {
      checkPage(6);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(MARGIN + 10, y, PAGE_W - MARGIN - 10, y);
      y += 6;
    }
  });

  // Footer on each page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text("Trades Toolbelt — Job Log Report", MARGIN, 290);
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W - MARGIN, 290, { align: "right" });
  }

  doc.save(`job-log-${new Date().toISOString().slice(0, 10)}.pdf`);
};
